import { useMemo } from "react";
import { useSearchParams } from "react-router-dom-v5-compat";
import dayjs from "dayjs";
import { ensureExhaustive } from "./types";

export const QueryParamType = {
  optionalString: "optionalString",
  requiredString: "requiredString",
  optionalNumber: "optionalNumber",
  requiredNumber: "requiredNumber",
  optionalDate: "optionalDate",
  requiredDate: "requiredDate",
  optionalLocalDateTime: "optionalLocalDateTime",
  requiredLocalDateTime: "requiredLocalDateTime",
  optionalBoolean: "optionalBoolean",
  requiredBoolean: "requiredBoolean",
  optionalStringArray: "optionalStringArray",
  requiredStringArray: "requiredStringArray",
  optionalNumberArray: "optionalNumberArray",
  requiredNumberArray: "requiredNumberArray",
} as const;

export type QueryParamType =
  (typeof QueryParamType)[keyof typeof QueryParamType];

type QueryParamArrayTypes =
  | (typeof QueryParamType)["optionalNumberArray"]
  | (typeof QueryParamType)["requiredNumberArray"]
  | (typeof QueryParamType)["optionalStringArray"]
  | (typeof QueryParamType)["requiredStringArray"];

export type QueryParamDefinition<T extends QueryParamType = QueryParamType> = {
  [K in T]: {
    type: K;
    parse?: (value: NonNullable<QueryParamValueForType<K>>) => unknown;
  };
}[T];

const DATE_FORMAT = "YYYY-MM-DD";
const LOCAL_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm";

const parseLocalDateTime = (value: string): string => {
  const parsed = dayjs(value, LOCAL_DATE_TIME_FORMAT);

  if (!parsed.isValid()) {
    throw new Error(
      `Invalid date format [${value}]. Expected format: ${LOCAL_DATE_TIME_FORMAT}`
    );
  }

  return parsed.format(LOCAL_DATE_TIME_FORMAT);
};

const parseBoolean = (value: string): boolean => {
  const lowerValue = value.toLowerCase();
  if (lowerValue === "true" || lowerValue === "t" || lowerValue === "1")
    return true;
  if (lowerValue === "false" || lowerValue === "f" || lowerValue === "0")
    return false;
  throw new Error("Invalid boolean format");
};

export const QueryParamParsers = {
  [QueryParamType.requiredString]: (value: string): string => {
    if (value == null) {
      throw new Error("Missing required string parameter");
    }
    return value;
  },

  [QueryParamType.optionalString]: (value: string | null): string | null => {
    return value ?? null;
  },

  [QueryParamType.requiredNumber]: (value: string): number => {
    if (value == null) {
      throw new Error("Missing required number parameter");
    }
    const parsed = Number(value);
    if (isNaN(parsed)) {
      throw new Error("Invalid number format");
    }
    return parsed;
  },

  [QueryParamType.optionalNumber]: (value: string | null): number | null => {
    if (value == null) {
      return null;
    }
    const parsed = Number(value);
    if (isNaN(parsed)) {
      throw new Error("Invalid number format");
    }
    return parsed;
  },

  [QueryParamType.requiredDate]: (value: string): string => {
    if (value == null) {
      throw new Error("Missing required date parameter");
    }
    const parsed = dayjs(value, DATE_FORMAT);
    if (!parsed.isValid()) {
      throw new Error(
        `Invalid date format [${value}]. Expected ${DATE_FORMAT}`
      );
    }
    return parsed.format(DATE_FORMAT);
  },

  [QueryParamType.optionalDate]: (value: string | null): string | null => {
    if (value == null) {
      return null;
    }
    const parsed = dayjs(value, DATE_FORMAT);
    if (!parsed.isValid()) {
      throw new Error(
        `Invalid date format [${value}]. Expected ${DATE_FORMAT}`
      );
    }
    return parsed.format(DATE_FORMAT);
  },

  [QueryParamType.requiredLocalDateTime]: (value: string): string => {
    if (value == null) {
      throw new Error("Missing required date parameter");
    }
    return parseLocalDateTime(value);
  },

  [QueryParamType.optionalLocalDateTime]: (
    value: string | null
  ): string | null => {
    if (value == null) {
      return null;
    }
    return parseLocalDateTime(value);
  },

  [QueryParamType.optionalBoolean]: (value: string | null): boolean | null => {
    if (value == null) return null;
    return parseBoolean(value);
  },

  [QueryParamType.requiredBoolean]: (value: string): boolean => {
    if (value == null) throw new Error("Missing required boolean parameter");
    return parseBoolean(value);
  },

  [QueryParamType.requiredStringArray]: (
    values: string[]
  ): [string, ...string[]] => {
    if (values.length === 0) {
      throw new Error("Missing required string array parameter");
    }
    return values as [string, ...string[]];
  },

  [QueryParamType.optionalStringArray]: (values: string[]): string[] | null => {
    if (values.length === 0) {
      return null;
    }
    return values;
  },

  [QueryParamType.requiredNumberArray]: (
    values: string[]
  ): [number, ...number[]] => {
    if (values.length === 0) {
      throw new Error("Missing required number array parameter");
    }

    const parsed = values.map(Number);
    if (parsed.some(isNaN)) {
      throw new Error("Invalid number format in array");
    }

    return parsed as [number, ...number[]];
  },

  [QueryParamType.optionalNumberArray]: (values: string[]): number[] | null => {
    if (values.length === 0) {
      return null;
    }

    const parsed = values.map(Number);
    if (parsed.some(isNaN)) {
      throw new Error("Invalid number format in array");
    }
    return parsed;
  },
} satisfies Record<QueryParamType, (val: unknown) => unknown>;

export type QueryParamValueForType<T extends QueryParamType> = ReturnType<
  (typeof QueryParamParsers)[T]
>;

export type QueryParamValue<T extends QueryParamDefinition> = T extends {
  parse: (...args: unknown[]) => infer Result;
}
  ? null extends QueryParamValueForType<T["type"]>
    ? Result | null
    : Result
  : QueryParamValueForType<T["type"]>;

export const parseQueryParams = <
  Definitions extends {
    [k in string]: QueryParamDefinition;
  },
>(
  searchParams: URLSearchParams,
  definitions: Definitions
): { [K in keyof Definitions]: QueryParamValue<Definitions[K]> } => {
  return Object.entries(definitions).reduce((acc, [key, definition]) => {
    const { type } = definition;
    const values = searchParams.getAll(key);

    const parser = QueryParamParsers[type];
    if (parser == null) {
      throw new Error(`Unsupported parameter type: ${type}`);
    }

    try {
      const parsed =
        type === QueryParamType.requiredNumberArray ||
        type === QueryParamType.requiredStringArray ||
        type === QueryParamType.optionalNumberArray ||
        type === QueryParamType.optionalStringArray
          ? (parser as (typeof QueryParamParsers)[QueryParamArrayTypes])(values)
          : (
              parser as (typeof QueryParamParsers)[Exclude<
                QueryParamType,
                QueryParamArrayTypes
              >]
            )(values[0]);
      return {
        ...acc,
        [key]:
          definition["parse"] != null && parsed != null
            ? // This cast is hacky. The type of the `parse` function does
              // resolve properly to a union, but it appears that since the
              // union doesn't have overlap, the unified parameter type is
              // `never`.
              definition["parse"](parsed as never)
            : parsed,
      };
    } catch (e) {
      throw new Error(
        `Error parsing the key [${key}] of type [${definition.type}] with value [${values}]. Error: {${e}}`
      );
    }
  }, {} as any);
};

type ParamType =
  | string
  | number
  | Date
  | DateTime
  | Array<string>
  | Array<number>
  | Array<Date>
  | Array<DateTime>
  | undefined
  | null
  | boolean;

export const DateTimeType = {
  local: "local",
  normal: "normal",
} as const;
export type DateTimeType = (typeof DateTimeType)[keyof typeof DateTimeType];

type DateTime = {
  value: Date;
  type: DateTimeType;
};

export const createQueryParams = <T extends Record<string, ParamType>>(
  params: T
): URLSearchParams => {
  return Object.entries(params).reduce((searchParams, [key, value]) => {
    if (value == null) return searchParams;

    if (Array.isArray(value)) {
      value.forEach((v) => {
        searchParams.append(key, formatValue(v));
      });
      return searchParams;
    }

    if (value != null) {
      searchParams.set(key, formatValue(value));
    }

    return searchParams;
  }, new URLSearchParams());
};

const isDateTimeValue = (value: ParamType): value is DateTime => {
  return (
    typeof value === "object" &&
    value !== null &&
    "value" in value &&
    "type" in value &&
    (value.type === "local" || value.type === "normal") &&
    value.value instanceof Date
  );
};

const formatValue = (value: ParamType): string => {
  if (isDateTimeValue(value)) {
    switch (value.type) {
      case "local":
        return dayjs(value.value).format(LOCAL_DATE_TIME_FORMAT);

      case "normal":
        throw new Error(`Un-implemented type: ${value.type}`);

      default:
        ensureExhaustive(value.type, undefined);
        throw new Error(`Unsupported DateTime type: ${value.type}`);
    }
  }
  if (value instanceof Date) {
    return dayjs(value).format(DATE_FORMAT);
  }
  return String(value);
};

export const useParseQueryParams = <
  Definitions extends Record<
    string,
    QueryParamDefinition //| ParsedQueryParamDefinition<QueryParamType, any>
  >,
>(
  definitions: Definitions
): { [K in keyof Definitions]: QueryParamValue<Definitions[K]> } => {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    return parseQueryParams(searchParams, definitions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), JSON.stringify(definitions)]);
};
